import React, { useState, useEffect } from 'react';
import API from '../../../Utils/api';
import UploadImg from '../../../Utils/UploadImg';
import toast from 'react-hot-toast';

const EditTestimonialModal = ({ testimonial, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    heading: '',
    content: '',
    authorName: '',
    jobRole: '',
    rating: '5',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (testimonial) {
      setForm({
        heading: testimonial.heading || '',
        content: testimonial.content || '',
        authorName: testimonial.authorName || '',
        jobRole: testimonial.jobRole || '',
        rating: String(testimonial.rating) || '5',
      });
      setPreview(testimonial.logo || null);
    }
  }, [testimonial]);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let logoUrl = testimonial.logo;

      if (logoFile) {
        logoUrl = await UploadImg(logoFile);
      }

      await API.put(`/testimonial/edit/${testimonial._id}`, {
        ...form,
        logo: logoUrl,
        rating: Number(form.rating),
      });

      toast.success('Testimonial updated successfully');
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong');
      toast.error('Failed to update testimonial');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-800">Edit Testimonial</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 transition"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Logo Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Logo / Photo</label>
            <div
              onClick={() => document.getElementById('edit-testimonial-logo').click()}
              className="border-2 border-dashed border-gray-300 hover:border-green-400 rounded-xl p-4 cursor-pointer transition flex items-center gap-4"
            >
              {preview ? (
                <img src={preview} alt="preview" className="w-16 h-16 object-cover rounded-xl" />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-gray-100 flex items-center justify-center text-gray-300 text-2xl">
                  🖼️
                </div>
              )}
              <div className="text-sm text-gray-400">
                <p className="font-medium text-gray-600">Click to change logo</p>
                <p className="text-xs mt-0.5">PNG, JPG recommended</p>
              </div>
            </div>
            <input
              id="edit-testimonial-logo"
              type="file"
              accept="image/*"
              onChange={handleLogoChange}
              className="hidden"
            />
          </div>

          {/* Heading */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Heading</label>
            <input
              type="text"
              name="heading"
              value={form.heading}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Review Content</label>
            <textarea
              name="content"
              value={form.content}
              onChange={handleChange}
              rows={4}
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 resize-none"
            />
          </div>

          {/* Author Name + Job Role */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Author Name</label>
              <input
                type="text"
                name="authorName"
                value={form.authorName}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Job Role</label>
              <input
                type="text"
                name="jobRole"
                value={form.jobRole}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
              />
            </div>
          </div>

          {/* Rating */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Rating</label>
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5].map(star => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, rating: String(star) }))}
                  className={`text-3xl transition ${
                    star <= Number(form.rating) ? 'text-yellow-400' : 'text-gray-200'
                  }`}
                >
                  ★
                </button>
              ))}
              <span className="text-sm text-gray-500 ml-2">{form.rating} / 5</span>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white font-medium transition disabled:opacity-60"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTestimonialModal;